/* eslint-disable camelcase */
import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { RESPONSES_QUERY_KEYS } from 'components/responses/constants';
import { api } from 'controllers/network/apiClient';

export interface Shop {
  id: number;
  name: string;
}

export interface Subscriber {
  id: number;
  phone: string;
  first_name?: string;
  last_name?: string;
  email?: string;
}

export interface ConversationMessage {
  oid?: number;
  external_id?: string;
  direction: string;
  body: string;
  type?: string;
  type_id?: number;
  user_id?: number;
  created_at: string;
  tags?: string[];
  sent_message_id?: number;
  subtype?: string;
}

export interface TopicLabel {
  id: any;
  label: string;
  rank: number;
  version: string;
}

export interface ConversationInsights {
  created_at: string;
  conversation_id: string;
  summary: string;
  labels: string[];
  topic_labels: TopicLabel[];
  sentiment?: string;
  title?: string;
}

export interface ConversationInsightsSubscriber {
  id: number;
  phone_number: string;
  first_name?: string;
  last_name?: string;
  email?: string;
}

export interface ConversationInsightsSubscriberOutcome {
  type: string;
  created_at?: string;
  subscriber_id: number;
  conversation_id: string;
}

export interface ConversationWithInsights {
  id: string;
  subscriber?: ConversationInsightsSubscriber;
  shop?: Shop;
  active: boolean;
  created_at: string;
  updated_at: string;
  expiry_at: string;
  closed_at: string;
  messages: ConversationMessage[];
  outcomes?: Array<ConversationInsightsSubscriberOutcome>;
  tags?: string[];
  insights?: ConversationInsights[];
}

export interface ConversationIntelligenceUser {
  id: number;
  guid: string;
  username: string;
  first_name?: string;
  last_name?: string;
  avatar_url?: string;
}

export interface ConversationsWithInsightsResponse {
  conversations: ConversationWithInsights[];
  users?: ConversationIntelligenceUser[];
  matching_conversations: number;
  total_conversations: number;
  active_conversations: number;
  matching_unique_subscribers: number;
  total_unique_subscribers: number;
}

/**
 * Raw, on-the-wire shapes. The backend serializer types an insight's
 * collection fields as optional and seeds them lazily, so a partially
 * populated insights doc can send `labels`/`topic_labels` as `null`.
 * `normalizeConversationsResponse` coerces them to arrays at the fetch
 * boundary so no component has to null-check before iterating — this is what
 * crashed ConversationSummaries (`labels.forEach` on `null`).
 */
export interface RawConversationInsights
  extends Omit<ConversationInsights, 'labels' | 'topic_labels'> {
  labels: string[] | null;
  topic_labels: TopicLabel[] | null;
}

export interface RawConversationWithInsights
  extends Omit<ConversationWithInsights, 'insights'> {
  insights?: RawConversationInsights[] | null;
}

export interface RawConversationsWithInsightsResponse
  extends Omit<ConversationsWithInsightsResponse, 'conversations'> {
  conversations: RawConversationWithInsights[] | null;
}

const normalizeInsight = (
  insight: RawConversationInsights,
): ConversationInsights => ({
  ...insight,
  labels: insight.labels ?? [],
  topic_labels: insight.topic_labels ?? [],
});

const normalizeConversation = (
  conversation: RawConversationWithInsights,
): ConversationWithInsights => ({
  ...conversation,
  insights: conversation.insights?.map(normalizeInsight),
});

/**
 * Coerce every nullable insight collection to an array so downstream
 * components can iterate `labels`/`topic_labels` without per-call-site guards.
 * Apply this wherever the conversations-with-insights payload enters the app.
 */
export const normalizeConversationsResponse = (
  response: RawConversationsWithInsightsResponse,
): ConversationsWithInsightsResponse => ({
  ...response,
  conversations: (response.conversations ?? []).map(normalizeConversation),
});

export interface InsightsPayload {
  date_range_start?: string;
  date_range_end?: string;
  timezone: string;
  shop_ids?: number[] | string[];
  sales_agent_ids?: number[] | string[];
  search_text?: string;
  search_direction?: string;
  sort_by?: string;
  filters?: string[];
  topic_label?: string;
  tag?: string;
  conversation_tag?: string;
  has_insights?: boolean;
  max_page_size: number;
  page: number;
}

export const useSummaries = (
  payload: InsightsPayload,
  isMultiShop: boolean,
): UseQueryResult<ConversationsWithInsightsResponse> => {
  const route = isMultiShop
    ? '/v2/convert/conversations'
    : '/v2/convert/shop-conversations';
  return useQuery({
    queryKey: [RESPONSES_QUERY_KEYS.CONVERSATION_SUMMARIES, payload],
    queryFn: async (): Promise<ConversationsWithInsightsResponse> => {
      const response = await api.post<RawConversationsWithInsightsResponse>(
        route,
        payload,
      );
      return normalizeConversationsResponse(response);
    },
  });
};
